import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = user
  const [error, setError] = useState("");

  // Boot probe: figure out whether the user is currently authenticated.
  // Only this initial call is allowed to flip the user state to `false` —
  // any later `refresh()` (e.g. from a page mount) is non-destructive so
  // a transient network blip or iOS cookie quirk does NOT log the user out.
  const bootRefresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // No valid session — treat as anonymous.
      setToken(""); // also clear any stale token
      setUser(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Non-destructive: keep the existing user state. Only an explicit
      // logout (button) or initial bootRefresh may set user=false.
      // This avoids the iOS Safari edge case where a single failed /auth/me
      // (cookie temporarily dropped) would otherwise kick the user out.
    }
  }, []);

  useEffect(() => {
    bootRefresh();
  }, [bootRefresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the JWT so future requests can attach a Bearer header
      // even if the HttpOnly cookie is dropped by the browser (iOS Safari).
      if (data?.token) setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      return { ok: true, data };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setToken("");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
